import type { TiebreakerTeam, TiebreakerStepFn, StepResult, NhlTiebreakerTeam } from '../types';
import { RankMethod } from '../../enums/rank-method';
import { bestOf, addExplanation, addExplanationAll } from '../helpers';

/**
 * Generic record-percentage step factory.
 * Uses `bestOf` on a numeric field selector. Returns full/partial/no break.
 */
export function createRecordPctStep<T extends TiebreakerTeam>(
  fieldSelector: (team: T) => number,
  rankMethod: RankMethod,
  label: string,
): TiebreakerStepFn<T> {
  return (teams: T[], ctx): StepResult<T> => {
    const best = bestOf(teams, fieldSelector);

    if (best.length === teams.length) {
      // No break: all tied
      addExplanationAll(ctx, teams, `${label}: all tied.`);
      return { winners: [], remaining: teams };
    }

    if (best.length === 1 && teams.length === 2) {
      // Full break: 2-team, one winner
      const winner = best[0];
      const loser = teams.find(t => t !== winner)!;
      addExplanation(ctx, winner, `${label}: ${fieldSelector(winner).toFixed(3)}.`);
      addExplanation(ctx, loser, `${label}: ${fieldSelector(loser).toFixed(3)}.`);
      return { winners: [winner, loser], remaining: [], rankMethod };
    }

    if (best.length < teams.length) {
      // Partial break
      const remaining = teams.filter(t => !best.includes(t));
      for (const t of best) {
        addExplanation(ctx, t, `${label}: ${fieldSelector(t).toFixed(3)} (best).`);
      }
      return { winners: best, remaining, rankMethod };
    }

    return { winners: [], remaining: teams };
  };
}

// ─── Pre-built record steps ─────────────────────────────────────────────────

export const divPctStep = createRecordPctStep<TiebreakerTeam & { records: { division: { pctNaN: number } } }>(
  t => t.records.division.pctNaN,
  RankMethod.DivPCT,
  'Div%',
);

export const confPctStep = createRecordPctStep<TiebreakerTeam & { records: { conference: { pctNaN: number } } }>(
  t => t.records.conference.pctNaN,
  RankMethod.ConfPCT,
  'Conf%',
);

export const overallPctStep = createRecordPctStep<TiebreakerTeam & { records: { all: { pctNaN: number } } }>(
  t => t.records.all.pctNaN,
  RankMethod.AllPCT,
  'Overall%',
);

// ─── NHL-specific stat steps ────────────────────────────────────────────────

export const pointsPctStep = createRecordPctStep<NhlTiebreakerTeam>(
  t => t.pointsPct,
  RankMethod.PointsPct,
  'Points%',
);

export const regulationWinsStep = createRecordPctStep<NhlTiebreakerTeam>(
  t => t.regulationWins,
  RankMethod.RegulationWins,
  'RegWins',
);

export const rowStep = createRecordPctStep<NhlTiebreakerTeam>(
  t => t.row,
  RankMethod.ROW,
  'ROW',
);

export const totalWinsStep = createRecordPctStep<NhlTiebreakerTeam>(
  t => t.totalWins,
  RankMethod.TotalWins,
  'TotalWins',
);
